import {useEffect, useMemo, useState, useSyncExternalStore} from 'react'
import {currentBrand} from '../../../../_metronic/helpers/brand'
import {offersAndroidApp} from './androidApp'
import {currentInstallEnvironment, InstallEnvironment} from './installEnvironment'
import {installGuideFor} from './installGuides'
import {
  checkInstalledElsewhere,
  getInstallPromptState,
  isRunningInstalled,
  subscribeInstallPrompt,
  wasInstalledHere,
} from './installPrompt'

/** The installed app's name, as the manifests give it (short_name). */
export const installedAppName = () =>
  currentBrand.key === 'fash' ? currentBrand.name : 'معتمد آسا'

/** The installed app's icon, as the manifests give it (the bank's install: MegaPay's). */
export const installedAppIcon = () => currentBrand.appIcon || '/media/app-icons/icon-192.png'

/** «این آیفون», «این رایانه», …: the device the offer speaks of. */
export const thisDevice = (env: InstallEnvironment) => {
  switch (env.os) {
    case 'ios':
      return 'این آیفون'
    case 'ipados':
      return 'این آیپد'
    case 'android':
      return 'این گوشی'
    case 'macos':
      return 'این مک'
    case 'chromeos':
      return 'این کروم‌بوک'
    case 'windows':
    case 'linux':
      return 'این رایانه'
    default:
      return 'این دستگاه'
  }
}

/** The offer's one line under its title, for what this device and browser can do. */
export const installOfferLine = (
  env: InstallEnvironment,
  guide: ReturnType<typeof installGuideFor>
) => {
  // An Android phone gets the Android app, from any browser (its file downloads anywhere). The Latin
  // name at the line's end is kept whole and in its place by an isolate and a no-break space.
  if (offersAndroidApp(env))
    return 'برنامهٔ اندروید با ذخیرهٔ فایل، چاپ، اعلان‌های پنل و قفل اثر انگشت؛ بی‌نیاز از \u2068Google\u00a0Play\u2069.'
  if (env.inApp) return 'اول این صفحه را در Safari یا Chrome باز کنید؛ راهنما قدم‌به‌قدم می‌گوید.'
  if (guide.support === 'other-browser')
    return 'این مرورگر برنامه را نصب نمی‌کند؛ راهنما می‌گوید با کدام مرورگر نصب کنید.'
  if (env.mobile) return 'با یک لمس و تمام‌صفحه، بدون نوار مرورگر باز می‌شود.'
  return `در پنجرهٔ خودش و از ${guide.where} باز می‌شود.`
}

/**
 * Everything an install offer needs, the same wherever it is shown (sign-in page, dashboard,
 * «امکانات تازه», the guide): the device, its guide, the browser's one-click offer and whether the
 * panel is installed — running as the app, installed during this visit, reported by Chromium
 * (getInstalledRelatedApps) or installed from this browser before.
 */
export const useInstallState = () => {
  const prompt = useSyncExternalStore(subscribeInstallPrompt, getInstallPromptState)
  const detected = useMemo(currentInstallEnvironment, [])
  const guide = useMemo(() => installGuideFor(detected), [detected])
  const runningInstalled = useMemo(isRunningInstalled, [])
  const [installedElsewhere, setInstalledElsewhere] = useState<boolean>()

  useEffect(() => {
    if (runningInstalled) return
    let alive = true
    checkInstalledElsewhere().then((installed) => {
      if (alive) setInstalledElsewhere(installed)
    })
    return () => {
      alive = false
    }
  }, [runningInstalled])

  const installed =
    runningInstalled || prompt.installedNow || installedElsewhere === true || wasInstalledHere()

  return {prompt, detected, guide, runningInstalled, installedElsewhere, installed}
}
